import { useMemo } from 'react';
import { AssortmentPrices } from '@retail/my-assortment/types';
import { StackedTextCell } from '@retail/components';
import { GridColDef } from '@mui/x-data-grid-premium';
import { useAppTFunction } from '@retail/app/i18n';
import { Stack, Tooltip } from '@mui/material';
import { ConditionTooltipInfo } from '@retail/calculus/components';

const baseColumnProps: Partial<GridColDef> = {
  width: 130,
  headerName: '',
  headerAlign: 'left',
  align: 'left',
};

export const useConditionColumns = () => {
  const t = useAppTFunction();

  return useMemo(() => {
    const purchaseConditionColumn: GridColDef<AssortmentPrices> = {
      ...baseColumnProps,
      field: 'purchaseCondition',
      headerName: t('purchaseAgreements.condition'),
      width: 210,
      renderCell: ({ row }) => {
        const purchaseCondition = row.purchaseCondition;

        return (
          <Stack flexDirection="row">
            {purchaseCondition ? (
              <Tooltip
                title={
                  <ConditionTooltipInfo
                    title=""
                    bindingType={purchaseCondition.bindingType}
                    bindingTarget={purchaseCondition.bindingTarget}
                  />
                }
              >
                <Stack
                  border={(theme) => `1px solid ${theme.vars.palette.divider}`}
                  p={1}
                  borderRadius={2}
                  minWidth={({ spacing }) => spacing(14)}
                >
                  <StackedTextCell
                    nullableBottomLine
                    topLine={purchaseCondition.agreementText}
                    bottomLine={purchaseCondition.supplierAgreementId}
                  />
                </Stack>
              </Tooltip>
            ) : (
              '-'
            )}
          </Stack>
        );
      },
      valueGetter: (_, row) => row.purchaseCondition?.agreementText,
    };

    const freightConditionColumn: GridColDef<AssortmentPrices> = {
      ...baseColumnProps,
      field: 'freightCondition',
      headerName: t('freight.freightCondition'),
      align: 'center',
      headerAlign: 'center',
      width: 160,
      renderCell: ({ row }) => {
        const freightCondition = row.freightCondition;

        if (!freightCondition) return '-';

        const freightConditionValue = freightCondition.freightFixedAddOn
          ? t('freight.columns.pricePerUnit', {
              price: freightCondition.freightFixedAddOn,
              unit: freightCondition.freightFixedAddOnUnit,
            })
          : freightCondition.freightPercentAddon + '%';

        return (
          <Tooltip
            title={
              <ConditionTooltipInfo
                title=""
                bindingType={freightCondition.bindingType}
                bindingTarget={freightCondition.bindingTarget}
              />
            }
          >
            <Stack
              border={(theme) => `1px solid ${theme.vars.palette.divider}`}
              p={1}
              borderRadius={2}
            >
              {freightConditionValue}
            </Stack>
          </Tooltip>
        );
      },
      valueGetter: (_, row) =>
        row.freightCondition?.freightPercentAddon ?? row.freightCondition?.freightFixedAddOn,
    };

    return {
      purchaseConditionColumn,
      freightConditionColumn,
    };
  }, [t]);
};
